import { useEffect, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client'

import { driveTimeFilter } from '../featureFlags'
import useFeatureFlag from '../hooks/useFeatureFlag'
import useReduxSelector from '../hooks/useReduxSelector'
import { useDriveTimeFilterQuery } from '../hooks/useQueries'

import driveTimeScope from './driveTimeScope'

type DriveTimeFilterState = {
  available: boolean
  /** The resolved default origin when it is a city (scope city or pick) */
  originCitySlug?: string | null
  /** The visitor's own area can usefully filter the searched scope */
  visitorOriginUsable: boolean
  /** The visitor's origin bucket is being materialized server-side */
  warming: boolean
  /** Warming has outlived its grace period — likely a routing outage */
  warmingSlowly: boolean
  originCityName?: string | null
  /** False while eligibility is still being fetched — unknown, not ineligible */
  resolved: boolean
}

const POLL_WHILE_WARMING_MS = 5000
const WARMING_SLOW_AFTER_MS = 10000

// Results and drive times fetched while the bucket was cold; their query
// variables don't encode the origin, so only a refetch displaces them
const ORIGIN_DEPENDENT_QUERIES = ['search', 'listingDriveTimes', 'mapResults']

// Deliberately a separate client-only query: eligibility is viewer-dependent,
// so it must stay out of the SSR-seeded search document.
const useDriveTimeFilterAvailable = (
  filters: GQL_SearchFilters,
  { slowAfterMs = WARMING_SLOW_AFTER_MS }: { slowAfterMs?: number } = {},
): DriveTimeFilterState => {
  const flagEnabled = useFeatureFlag(driveTimeFilter)
  // Per-actor flags arrive via an async /features fetch after mount; until it
  // settles, "flag off" is indistinguishable from "unknown" and must not read
  // as resolved or the exposure marker records eligible visitors as ineligible.
  const flagsSettled = useReduxSelector(
    (state) => state.featureFlags.fetched || state.featureFlags.error != null,
  )
  const client = useApolloClient()
  const { data, previousData, error, startPolling, stopPolling } =
    useDriveTimeFilterQuery({
      variables: { scope: driveTimeScope(filters) },
      skip: !flagEnabled,
    })

  // Changing origin changes the variables, which empties `data` until the
  // new answer lands; holding the previous answer stops the filter group
  // blinking out mid-transition
  const filter = (data ?? previousData)?.driveTimes?.filter

  const warming = Boolean(filter?.warming)

  // Re-check eligibility until the bucket worker lands
  useEffect(() => {
    if (!warming) return undefined

    startPolling(POLL_WHILE_WARMING_MS)
    return () => stopPolling()
  }, [warming, startPolling, stopPolling])

  // Anything fetched against a cold bucket is stale the moment it warms and
  // would otherwise be re-served from the cache indefinitely
  const wasWarming = useRef(false)
  useEffect(() => {
    if (warming) {
      wasWarming.current = true
      return
    }
    if (!wasWarming.current) return

    wasWarming.current = false
    client.refetchQueries({ include: ORIGIN_DEPENDENT_QUERIES })
  }, [warming, client])

  const [warmingSlowly, setWarmingSlowly] = useState(false)
  useEffect(() => {
    if (!warming) {
      setWarmingSlowly(false)
      return undefined
    }

    const timer = setTimeout(() => setWarmingSlowly(true), slowAfterMs)
    return () => clearTimeout(timer)
  }, [warming, slowAfterMs])

  return {
    available: Boolean(filter?.available),
    visitorOriginUsable: Boolean(filter?.visitorOriginUsable),
    warming,
    warmingSlowly,
    originCitySlug: filter?.originCitySlug,
    originCityName: filter?.originCityName,
    resolved:
      flagsSettled &&
      (!flagEnabled ||
        data !== undefined ||
        previousData !== undefined ||
        error !== undefined),
  }
}

export default useDriveTimeFilterAvailable
